<template lang="html">
  <v-dialog
    scrollable
    :value="true"
    max-width="700"
    persistent
  >
    <v-form
      @submit.prevent="save"
      v-model="isValid"
      ref="form"
      style="width:100%"
    >
      <v-card :class="{large: !appSmall}">
        <v-card-title
          class="title grey lighten-2"
          primary-title
        >
          Export
        </v-card-title>

        <v-card-text class="pb-0 pt-3">
          <strong class="title">1. Select date range and machine</strong>
          <v-layout row wrap class="pl-3 pr-3 mb-3">
            <v-flex xs12 md6>
              <DateRangePicker
                dialog
                outlined
                v-model="dateRange"
              />
            </v-flex>
            <v-flex xs12 md6>
              <MachinesPicker
                v-model="machines"
                multiple
                outlined
              />
            </v-flex>
          </v-layout>
          <strong class="title">2. Select any filters for exported data</strong>
          <AlertConditionTypesPicker
            v-model="selectedFields"
            defaultSelectAll
            multiple
          />
          <br />
          <strong class="title">3. Select the format you wish to export the data</strong>

          <table class="table export-types">
            <tbody>
              <tr>
                <td width="200">Excel 97 - 2003</td>
                <td>
                  <v-btn
                    type="button"
                    color="primary"
                    :disabled="loading || !!exportError"
                    @click="selectFormat('xls')"
                  >.xls
                  </v-btn>
                </td>
              </tr>
              <tr>
                <td>Excel:</td>
                <td>
                  <v-btn
                    type="button"
                    color="primary"
                    :disabled="loading || !!exportError"
                    @click="selectFormat('xlsx')"
                  >.xlsx
                  </v-btn>
                </td>
              </tr>
              <tr>
                <td>HTML:</td>
                <td>
                  <v-btn
                    type="button"
                    color="primary"
                    :disabled="loading || !!exportError"
                    @click="selectFormat('html')"
                  >.html
                  </v-btn>
                </td>
              </tr>
              <tr>
                <td>Comma-Separated:</td>
                <td>
                  <v-btn
                    type="button"
                    color="primary"
                    :disabled="loading || !!exportError"
                    @click="selectFormat('csv')"
                  >.csv
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </v-card-text>

        <v-divider />

        <v-alert :value="true" type="error" style="width:100%" v-if="exportError">{{exportError}}</v-alert>

        <v-card-actions>
          <div>Exporting works in <strong>Chrome (version 49+) and Safari (version 10.1+)</strong>.</div>
          <v-spacer />
          <v-btn
            v-if="!loading"
            @click="$emit('close')"
            text
          >
            Close
          </v-btn>

          <v-progress-circular
            v-if="loading"
            class="ma-2"
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<style lang="scss" scoped>
.large {
  .layout {
    .flex:first-child {
      padding-right: 8px;
    }
    .flex:last-child {
      padding-left: 8px;
    }
  }
}
</style>

<script>
import get from 'lodash/get'
import ValidationMixin from '@/mixins/ValidationMixin'
import DateRangePicker from '@/components/DateRangePicker'
import MachinesPicker from '@/components/MachinesPicker'
import cloneDeep from 'lodash/cloneDeep'
import AlertConditionTypesPicker from '@/components/AlertConditionTypesPicker'
import moment from 'moment-timezone'

let sanitizeIncomingDateRange = (dateRange) => {
  // When date ranges support relative this wl
  return cloneDeep(dateRange)
}

export default {
  mixins: [ValidationMixin],
  components: {
    AlertConditionTypesPicker,
    DateRangePicker,
    MachinesPicker
  },
  props: {
    params: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    machineError () {
      if (!this.machines || !this.machines.length) {
        return 'Please select at least one machine.'
      }
      return null
    },
    dateRangeError () {
      if (!(this.dateRange && this.dateRange.start && this.dateRange.end)) {
        return 'Both dates must be selected.'
      }
      if (moment(this.dateRange.end).isBefore(moment(this.dateRange.start))) {
        return 'Start Date must be before End Date.'
      }
      if (moment(this.dateRange.end).subtract(93, 'days').isAfter(moment(this.dateRange.start))) {
        return 'You can only export a maximum of 90 days.'
      }
      return null
    },
    fieldsError () {
      if (!this.selectedFields || !this.selectedFields.length) {
        return 'Please select at least one field.'
      }
      return null
    },
    exportError () {
      return (this.machineError || this.dateRangeError || this.fieldsError)
    }
  },
  data () {
    return {
      dateRange: null,
      machines: null,
      selectedFields: [],
      error: null,
      loading: false
    }
  },
  methods: {
    async saveItem () {
      if (!this.isValid) {
        this.$refs.form.validate()
        return
      }
      this.error = null
      try {
        // @TODO
        this.$emit('close')
      } catch (e) {
        this.error = get(e, 'response.data') || e
        throw e
      }
    },
    async selectFormat (format) {
      this.loading = true
      await new Promise(resolve => setTimeout(resolve, 5000))
      let file = '/export/data/export?format=' + format + (this.dateRange && this.dateRange.end ? '&todate=' + moment(this.dateRange.end).format('YYYY-MM-DD hh:mm:ss') : '') + (this.dateRange && this.dateRange.start ? '&fromdate=' + moment(this.dateRange.start).format('YYYY-MM-DD hh:mm:ss') : '') + this.machines.map((machine) => '&machineid[]=' + machine).join('') + this.selectedFields.map((field) => '&fields[]=' + field).join('')

      let anchor = document.createElement('a')

      let headers = new Headers()
      headers.append('Authorization', 'Bearer ' + window.localStorage.token)

      let promise = fetch(file, { headers }).then(response => response.blob())
      promise.then(blobby => {
        let objectUrl = window.URL.createObjectURL(blobby)

        anchor.href = objectUrl
        anchor.download = 'export.' + format
        anchor.click()

        window.URL.revokeObjectURL(objectUrl)
        this.loading = false
      }).catch((error) => {
        this.loading = false
        throw error
      })
    }
  },
  async created () {
    if (this.params) {
      if (this.params.dateRange) {
        this.dateRange = sanitizeIncomingDateRange(this.params.dateRange)
      }
      if (this.params.machine) {
        this.machines = [this.params.machine]
      }
    }
  }
}
</script>
